import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MinusCircle } from 'lucide-react'
import { fehlertext } from '@/lib/fehlertext'
import { documentsApi } from '../api/documents.api'
import { formatBetrag, formatDatum } from '../lib/grid'
import type { DeductionCandidate, DocumentDetail } from '../types/document.types'

interface Props {
  open: boolean
  beleg: DocumentDetail
  onClose: () => void
  onApplied: (b: DocumentDetail) => void
}

/**
 * AP-32 Phase I — **Anzahlungen und Teilrechnungen abziehen.** Belege derselben
 * Kette oder desselben Projekts sind vorausgewählt; je Steuersatz entsteht eine
 * Zeile mit Menge −1, die Steuer rechnet der Kern zurück.
 */
export function DeductionsDialog({ open, beleg, onClose, onApplied }: Props) {
  const { t } = useTranslation()
  const [kandidaten, setKandidaten] = useState<DeductionCandidate[] | null>(null)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    if (!open) return
    setFehler(null)
    setKandidaten(null)
    documentsApi
      .deductionCandidates(beleg.id)
      .then(({ data }) => {
        setKandidaten(data.data)
        setAuswahl(new Set(data.data.filter((k) => k.related && !k.inThisDocument).map((k) => k.id)))
      })
      .catch((err) => {
        setFehler(fehlertext(err, t))
        setKandidaten([])
      })
  }, [open, beleg.id, t])

  if (!open) return null

  const umschalten = (id: string) =>
    setAuswahl((a) => {
      const neu = new Set(a)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })

  const anwenden = async () => {
    setFehler(null)
    setLaeuft(true)
    try {
      const { data } = await documentsApi.addDeductions(beleg.id, [...auswahl])
      onApplied(data.data)
      onClose()
    } catch (err) {
      setFehler(fehlertext(err, t))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg border border-border shadow-xl"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="border-b border-border p-4">
          <h2 className="text-lg font-semibold">{t('documents.deductions.title')}</h2>
          <p className="text-xs text-muted-foreground">{t('documents.deductions.subtitle')}</p>
        </div>
        <div className="flex-1 overflow-auto p-2">
          {kandidaten === null && <p className="p-2 text-sm text-muted-foreground">{t('common.loading')}</p>}
          {kandidaten?.length === 0 && !fehler && <p className="p-2 text-sm text-muted-foreground">{t('documents.deductions.empty')}</p>}
          <ul>
            {kandidaten?.map((k) => (
              <li key={k.id} className={`flex items-center gap-3 rounded px-2 py-1.5 hover:bg-muted/40 ${k.inThisDocument ? 'opacity-50' : ''}`}>
                <input type="checkbox" disabled={k.inThisDocument} checked={k.inThisDocument || auswahl.has(k.id)} onChange={() => umschalten(k.id)} />
                <span className="w-40 text-sm">
                  {t(`documents.kinds.${k.kind}`)} <span className="font-mono">{k.documentNumber}</span>
                </span>
                <span className="w-24 text-xs tabular-nums text-muted-foreground">{formatDatum(k.documentDate)}</span>
                <span className="flex-1 text-xs">
                  {k.related && <span className="rounded-full bg-primary/10 px-2 py-0.5 text-primary">{t('documents.deductions.related')}</span>}
                  {k.inThisDocument && <span className="ml-1 text-muted-foreground">{t('documents.deductions.inThis')}</span>}
                </span>
                <span className="w-28 text-right text-sm tabular-nums">{formatBetrag(k.netTotal, beleg.currency)}</span>
              </li>
            ))}
          </ul>
          {fehler && <div className="m-2 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border p-4">
          <p className="text-xs text-muted-foreground">{t('documents.deductions.hint')}</p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border bg-background px-3 py-2 text-sm hover:bg-card">
              {t('common.cancel')}
            </button>
            <button type="button" disabled={laeuft || auswahl.size === 0} onClick={() => void anwenden()} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50">
              <MinusCircle className="h-4 w-4" />
              {t('documents.deductions.apply', { count: auswahl.size })}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
